import { useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  CRITERION_IMPORTANCE_LABELS,
  EDUCATION_LEVELS,
  EDUCATION_LEVEL_LABELS,
  WORK_MODE_LABELS,
  answerIcpQuestionSchema,
  formatMoney,
  isCurrencyCode,
  type IcpAnswer,
  type IcpInterviewView,
  type JobDetail,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import type { InterviewStep } from '../api/icp-api';
import { dockedPanel, type RenderPanel } from './panel-parts';
import styles from './icp.module.css';

// ADR 0061, mockup screen 2: the interview in the docked panel. What was read from the job, then the profile assistant (Sonnet)
// asks one question at a time: tap options or type, or skip. Progress and the running cost sit at the bottom, and "Draft the
// profile now" works at any time. The assistant never sees a CV or a candidate.

const NO_CANDIDATES_HINT = 'Don’t name candidates in your answers.';
const PRACTICE_TAG = 'Practice mode — not Sonnet';
const MODEL_LINE = 'Sonnet 5.5 · medium effort';

// ---- What was read from the job ----

export interface JobReading {
  readonly line: string;
  readonly chips: readonly string[];
}

function wordsIn(...texts: readonly (string | null)[]): number {
  return texts.reduce((sum, text) => sum + (text ? text.split(/\s+/).filter(Boolean).length : 0), 0);
}

function numberParam(params: Readonly<Record<string, unknown>>, key: string): number | null {
  const value = params[key];
  return typeof value === 'number' ? value : null;
}

/** The salary budget in words, only for someone who may see it (§L23): the app never sends the amount to a viewer it is hidden from. */
export function budgetLineOf(detail: JobDetail): string | null {
  const { salaryBudget } = detail;
  if ((salaryBudget.restrictedFields ?? []).length > 0 || salaryBudget.maxMinor === null) return null;
  const currency = salaryBudget.currency;
  if (currency === null || !isCurrencyCode(currency)) return null;
  const top = formatMoney({ amountMinor: BigInt(salaryBudget.maxMinor), currency });
  if (salaryBudget.minMinor === null) return `up to ${top}`;
  return `${formatMoney({ amountMinor: BigInt(salaryBudget.minMinor), currency })}–${top}`;
}

/** "Logistics Coordinator · Chattogram · 2+ years · ৳30,000–৳45,000 · Bachelor's", and a chip per criterion. */
export function jobReadingOf(detail: JobDetail): JobReading {
  const years = detail.criteria.map((criterion) => numberParam(criterion.params, 'min_years')).find((value) => value !== null) ?? null;
  const degree = detail.criteria.find((criterion) => criterion.type === 'education_level');
  const level = EDUCATION_LEVELS.find((entry) => entry === degree?.params['min_level']);
  const degreeLabel = level === undefined ? null : EDUCATION_LEVEL_LABELS[level];
  const budget = budgetLineOf(detail);
  const parts = [detail.title, detail.location, detail.workMode === 'onsite' ? null : WORK_MODE_LABELS[detail.workMode], years === null ? null : `${years}+ years`, budget, degreeLabel];
  const words = wordsIn(detail.description, detail.responsibilities, detail.requirements);
  return {
    line: parts.filter((part): part is string => part !== null && part !== '').join(' · '),
    chips: [...detail.criteria.map((criterion) => `${criterion.label} (${CRITERION_IMPORTANCE_LABELS[criterion.importance]})`), ...(words > 0 ? [`Description: ${words.toLocaleString('en-US')} words`] : [])],
  };
}

// ---- The panel ----

interface IcpInterviewPanelProps {
  readonly detail: JobDetail;
  readonly interview: IcpInterviewView | null | undefined;
  /** What the assistant is working on now. */
  readonly step: InterviewStep | null;
  /** "0.9¢", worked out from the tokens by the shared helper. */
  readonly costLabel: string | null;
  readonly onStart: () => void;
  readonly onAnswer: (turn: number, answer: { readonly choices: readonly string[]; readonly text: string | null; readonly isSkipped: boolean }) => void;
  readonly onNext: () => void;
  readonly onDraft: () => void;
  readonly onClose: () => void;
  /** ADR 0070 stage 2: what is left of the job's Jev budget, and why answering and drafting are off when nothing is (the interview is priced by use). */
  readonly budget?: { readonly suffix: string; readonly blockedNote: string | null };
  /** Draws the title, body and footer somewhere other than the docked panel (job setup draws them in its own card). */
  readonly renderPanel?: RenderPanel;
}

interface Pending {
  readonly turn: number;
  readonly choices: readonly string[];
  readonly text: string;
}

function answerText(answer: IcpAnswer): string {
  if (answer.isSkipped) return 'Skipped.';
  return [...answer.choices, ...(answer.text ? [answer.text] : [])].join(', ');
}

export function IcpInterviewPanel({ detail, interview, step, costLabel, onStart, onAnswer, onNext, onDraft, onClose, budget, renderPanel = dockedPanel }: IcpInterviewPanelProps): ReactNode {
  const [pending, setPending] = useState<Pending | null>(null);
  const reading = jobReadingOf(detail);
  const turns = interview?.turns ?? [];
  const openIndex = turns.findIndex((turn) => turn.answer === null);
  const open = openIndex >= 0 ? turns[openIndex] : undefined;
  // What is picked or typed belongs to the open question; a new question starts clean.
  const picked = pending && pending.turn === openIndex ? pending : { turn: openIndex, choices: [], text: '' };
  const isWorking = step === 'start' || step === 'answer' || step === 'next';
  const isDrafting = step === 'draft';
  const isDone = interview !== null && interview !== undefined && open === undefined && interview.isDone;
  const check = answerIcpQuestionSchema.safeParse({ turn: openIndex, choices: [...picked.choices], text: picked.text.trim() === '' ? null : picked.text.trim(), isSkipped: false });
  const typedProblem = picked.text.trim() !== '' && !check.success ? (check.error.issues[0]?.message ?? null) : null;
  const isOverBudget = Boolean(budget?.blockedNote);
  const canSend = open !== undefined && check.success && !isWorking && !isOverBudget;

  function send(answer: { readonly choices: readonly string[]; readonly text: string | null; readonly isSkipped: boolean }): void {
    if (open === undefined) return;
    setPending(null);
    onAnswer(openIndex, answer);
  }

  function submit(): void {
    if (canSend) send({ choices: picked.choices, text: picked.text.trim() === '' ? null : picked.text.trim(), isSkipped: false });
  }

  function toggle(option: string): void {
    if (!open) return;
    const isOn = picked.choices.includes(option);
    const choices = open.question.allowMultiple ? (isOn ? picked.choices.filter((choice) => choice !== option) : [...picked.choices, option]) : isOn ? [] : [option];
    setPending({ turn: openIndex, choices, text: picked.text });
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    submit();
  }

  const progressTotal = Math.max(interview?.expectedTotal ?? 0, turns.length, 1);
  const questionNumber = openIndex >= 0 ? openIndex + 1 : Math.max(turns.length, 1);
  const isPractice = interview?.provider === 'practice';

  const footer = (
    <>
      {interview && (
        <div className={styles.progress}>
          <span>{isDone ? `${turns.length} questions answered` : `Question ${questionNumber} of about ${progressTotal}`}</span>
          <span className={styles.bar} aria-hidden>
            <span className={styles.barFill} style={{ inlineSize: `${Math.min(100, Math.round((100 * (isDone ? progressTotal : turns.length)) / progressTotal))}%` }} />
          </span>
        </div>
      )}
      {open !== undefined && (
        <div className={styles.say}>
          {open.question.allowFreeText ? (
            <div className={styles.sayField}>
              <Input
                aria-label="Your answer"
                placeholder={open.question.options.length > 0 ? 'Type an answer, or tap an option above' : 'Type an answer'}
                value={picked.text}
                isInvalid={typedProblem !== null}
                disabled={isWorking}
                onChange={(event) => setPending({ turn: openIndex, choices: picked.choices, text: event.target.value })}
                onKeyDown={onKeyDown}
              />
            </div>
          ) : (
            <p className={styles.hint}>Pick an option, or skip.</p>
          )}
          <Button variant="solid" disabled={!canSend} onClick={submit}>
            Send
          </Button>
          <Button variant="ghost" disabled={isWorking} isBlocked={isOverBudget} onClick={() => send({ choices: [], text: null, isSkipped: true })}>
            Skip
          </Button>
        </div>
      )}
      {typedProblem && (
        <p className={styles.error} role="alert">
          {typedProblem}
        </p>
      )}
      <p className={styles.hint}>{NO_CANDIDATES_HINT}</p>
      <div className={styles.cost}>
        <span className={isPractice ? styles.practice : undefined}>{isPractice ? PRACTICE_TAG : MODEL_LINE}</span>
        {costLabel !== null && (
          <span>
            This interview so far: <b>{costLabel}</b>
            {budget?.suffix}
          </span>
        )}
        {costLabel === null && budget?.suffix ? <span>{budget.suffix.slice(3)}</span> : null}
      </div>
      <div className={styles.footRow}>
        <Button variant={isDone ? 'solid' : 'outline'} isPending={isDrafting} disabled={!interview || isWorking} isBlocked={isOverBudget} onClick={onDraft}>
          Draft the profile now
        </Button>
      </div>
      {budget?.blockedNote && (
        <p className={styles.error} role="status">
          {budget.blockedNote}
        </p>
      )}
    </>
  );

  const body = (
    <>
      <div className={styles.read}>
        <span className={styles.eyebrow}>Read from the job</span>
        <span>{reading.line}</span>
        <div className={styles.chips}>
          {reading.chips.map((chip) => (
            <span key={chip} className={styles.chip}>
              {chip}
            </span>
          ))}
        </div>
      </div>

      {turns.map((turn, index) => (
        <Turn key={`${index}:${turn.question.text}`} turn={turn} isOpen={index === openIndex} picked={index === openIndex ? picked.choices : turn.answer?.choices ?? []} isDisabled={isWorking} onToggle={toggle} />
      ))}

      {isDone && (
        <div className={styles.msg}>
          <span className={styles.who}>Profile assistant</span>
          <p className={styles.bubble}>I have enough to draft the profile. Draft it now, or change your last answer first.</p>
        </div>
      )}

      {isWorking && (
        <div className={styles.msg} aria-live="polite">
          <span className={styles.who}>Profile assistant</span>
          <p className={`${styles.bubble} ${styles.thinking}`}>thinking…</p>
        </div>
      )}

      {interview?.problem && !isWorking && (
        <div className={styles.inlineProblem} role="alert">
          <span>{interview.problem}</span>
          <Button size="sm" variant="outline" onClick={onNext}>
            Try again
          </Button>
        </div>
      )}

      {!interview && !isWorking && (
        <div className={styles.inlineProblem}>
          <span>The interview hasn’t started.</span>
          <Button size="sm" variant="solid" onClick={onStart}>
            Start the interview
          </Button>
        </div>
      )}
    </>
  );

  return renderPanel({ title: 'Build ideal candidate', sub: 'Answer by tapping an option or typing. Skip anything you don’t know.', onClose, footer, children: body });
}

interface TurnProps {
  readonly turn: IcpInterviewView['turns'][number];
  readonly isOpen: boolean;
  readonly picked: readonly string[];
  readonly isDisabled: boolean;
  readonly onToggle: (option: string) => void;
}

/** One question with its options, and the person's answer under it once given. */
function Turn({ turn, isOpen, picked, isDisabled, onToggle }: TurnProps): ReactNode {
  return (
    <>
      <div className={styles.msg}>
        <span className={styles.who}>Profile assistant</span>
        <p className={styles.bubble}>{turn.question.text}</p>
        {turn.question.options.length > 0 && (
          <div className={styles.options} role="group" aria-label={turn.question.allowMultiple ? 'Pick any' : 'Pick one'}>
            {turn.question.options.map((option) => (
              <button key={option} type="button" className={styles.option} aria-pressed={picked.includes(option)} disabled={!isOpen || isDisabled} onClick={() => onToggle(option)}>
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
      {turn.answer && (
        <div className={`${styles.msg} ${styles.msgMe}`}>
          <p className={styles.bubble}>{answerText(turn.answer)}</p>
        </div>
      )}
    </>
  );
}
